import React, { useCallback } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { CDN_TYPE_SPORT, getCDNUrl } from 'utils';

import {
  DedicatedEventProvider,
  groupMarketTypeInstructions,
  SiteConfigManager,
  SportContext,
  TournamentContext,
  useSportsbookState,
  useSportState,
} from '@atlas/core';
import { Flag, Icon } from '@atlas/ui';

import initialTournamentShape from 'common/PropTypes/initialTournamentShape';
import { eventClickHandler } from 'components/Pages/Prematch/CategoryTournamentsList';
import TournamentLoader from 'components/Pages/Prematch/TournamentLoader';
import { TournamentOutrights } from 'components/Pages/TournamentOutrights';
import { TournamentScrollGrid } from 'components/Pages/TournamentScrollGrid';
import { TournamentSwipeGrid } from 'components/Pages/TournamentSwipeGrid';
import useSelectedMarketType from 'hooks/useSelectedMarketType';

const SelectionTournaments = ({ tournaments }) => {
  const { sports } = useSportsbookState();
  const TournamentGridComponent = React.useMemo(() => {
    const mobileGridViewType = SiteConfigManager.getConfig(
      'config.config.sportsbook.mobileGridViewType',
    );

    if (mobileGridViewType === 'swipe') {
      return TournamentSwipeGrid;
    }

    return TournamentScrollGrid;
  }, []);

  const groups = Object.values(tournaments).reduce((acc, tournament) => {
    if (Array.isArray(acc[tournament.sportId])) {
      acc[tournament.sportId].push(tournament);
    } else {
      acc[tournament.sportId] = [tournament];
    }

    return acc;
  }, {});

  return Object.keys(groups).reduce((acc, sportId) => {
    const sport = sports.find(sport => `${sport.id}` === `${sportId}`);

    if (sport) {
      acc.push(
        <SportContext.Provider key={sport.id} value={sport}>
          <SelectionTournamentsList
            component={TournamentGridComponent}
            onEventClick={eventClickHandler}
            tournaments={groups[sportId]}
          />
        </SportContext.Provider>,
      );
    }

    return acc;
  }, []);
};

SelectionTournaments.propTypes = {
  tournaments: PropTypes.arrayOf(initialTournamentShape).isRequired,
};

const SelectionTournamentsList = props => {
  const { component: TournamentComponent, onEventClick, tournaments } = props;

  const { mainMarketTypes } = useSportState();
  const selectedMarketType = useSelectedMarketType();

  return tournaments.map(tournament => {
    const sportMarketTypesPeriodMap = groupMarketTypeInstructions(mainMarketTypes, {
      isMultilines: !!(
        tournament.sportFormMainTypes &&
        SiteConfigManager.getConfig('config.config.sportsbook.showMobileMultilines')
      ),
      selectedMarketType,
      tournamentMainTypes: tournament.sportFormMainTypes,
    });

    return (
      <TournamentContext.Provider key={tournament.id} value={tournament}>
        <SelectionsTournamentWrapper
          categoryName={tournament.categoryName}
          id={tournament.id}
          imagePaths={tournament.imagePaths}
          name={tournament.name}
        >
          <TournamentLoader>
            <TournamentComponent
              eventProvider={DedicatedEventProvider}
              hideMarketTypeHeaders
              hideOutcomeTypeHeaders
              marketTypeClassName="col"
              marketTypesPeriodMap={sportMarketTypesPeriodMap}
              onEventClick={onEventClick}
            />
            <TournamentOutrights />
          </TournamentLoader>
        </SelectionsTournamentWrapper>
      </TournamentContext.Provider>
    );
  });
};

SelectionTournamentsList.propTypes = {
  component: PropTypes.element,
  onEventClick: PropTypes.func.isRequired,
  tournaments: PropTypes.arrayOf(initialTournamentShape).isRequired,
};

const SelectionsTournamentWrapper = ({ categoryName, children, id, imagePaths, name }) => {
  const [isExpanded, setIsExpanded] = React.useState(true);
  const imagePath = imagePaths?.FLAG;

  const toggleExpand = useCallback(() => {
    setIsExpanded(isExpanded => !isExpanded);
  }, []);

  const iconClassName = cx(!isExpanded && 'arrow-down', isExpanded && 'arrow-up');

  return (
    <div className="tournament-table">
      <div className="tournament-header-info" onClick={toggleExpand}>
        <div className="menu-item-container sport">
          <div className="menu-item-icon">
            <div className="menu-item-icon icon-championship">
              <Flag
                alt={name}
                cdnHostname={getCDNUrl(CDN_TYPE_SPORT)}
                height={16}
                imagePath={imagePath}
                name={id}
                width={20}
              />
            </div>
          </div>
          <div className="menu-item-text">
            <span className="main-label">
              {categoryName}
              {', '}
              {name}
            </span>
          </div>
          <div className="menu-item-icon-controls">
            <div className="font-icon-container">
              <Icon className={iconClassName} name={iconClassName} />
            </div>
          </div>
        </div>
      </div>

      {isExpanded && <div className="tournament">{children}</div>}
    </div>
  );
};

export default SelectionTournaments;

SelectionsTournamentWrapper.propTypes = {
  categoryName: PropTypes.string,
  children: PropTypes.node,
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  imagePaths: PropTypes.shape({
    FLAG: PropTypes.string,
  }),
  name: PropTypes.string,
};
